import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Navigation,
  Plus,
  Trash2,
  Car,
  DoorOpen,
  Loader2,
  X,
  Check,
  Pencil,
  Search,
  Building2,
  Footprints,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useClientContext } from '../ClientContext';
import {
  getClientLocations,
  getClientOffices,
  createClientLocation,
  updateClientLocation,
  deleteClientLocation,
  type ClientLocation,
  type LocationType,
  LOCATION_TYPE_LABELS,
  LOCATION_TYPE_COLORS,
} from '@/api/client-locations';
import { searchAddress, type GeocodingResult } from '@/api/geo';
import { MapView, type EditPinState } from '../components/MapView';

const LOCATION_TYPE_ICONS: Record<LocationType, typeof Car> = {
  PARKING: Car,
  ENTRANCE: DoorOpen,
};

interface LocationFormData {
  type: LocationType;
  name: string;
  note: string;
  officeId: string | null;
}

type EditMode = 'add' | 'edit' | null;

export function LocationTab() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { clientId } = useClientContext();

  // State
  const [selectedLocation, setSelectedLocation] = useState<ClientLocation | null>(null);
  const [editMode, setEditMode] = useState<EditMode>(null);
  const [editPinPosition, setEditPinPosition] = useState<EditPinState | null>(null);
  const [formData, setFormData] = useState<LocationFormData>({
    type: 'PARKING',
    name: '',
    note: '',
    officeId: null,
  });
  const [navMode, setNavMode] = useState<'auto' | 'pedestrian'>('auto');

  // Search state
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GeocodingResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Queries
  const { data: locations = [], isLoading } = useQuery({
    queryKey: ['client-locations', clientId],
    queryFn: () => getClientLocations(clientId),
    enabled: !!clientId,
  });

  const { data: offices = [] } = useQuery({
    queryKey: ['client-offices', clientId],
    queryFn: () => getClientOffices(clientId),
    enabled: !!clientId,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: { type: LocationType; name: string; latitude: number; longitude: number; note?: string; officeId?: string | null }) =>
      createClientLocation(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-locations', clientId] });
      handleCancelEdit();
      toast.success('Точка добавлена');
    },
    onError: (error: unknown) => {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(message || 'Ошибка добавления точки');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { type?: LocationType; name?: string; latitude?: number; longitude?: number; note?: string; officeId?: string | null } }) =>
      updateClientLocation(clientId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-locations', clientId] });
      handleCancelEdit();
      toast.success('Точка обновлена');
    },
    onError: () => toast.error('Ошибка обновления'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteClientLocation(clientId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-locations', clientId] });
      setSelectedLocation(null);
      toast.success('Точка удалена');
    },
    onError: () => toast.error('Ошибка удаления'),
  });

  // Handlers
  const handleStartAdd = useCallback(() => {
    // Place pin at first existing location or Minsk center
    const initialPosition = locations.length > 0
      ? { lat: locations[0].latitude, lon: locations[0].longitude }
      : { lat: 53.9, lon: 27.5667 };

    // Default to ENTRANCE if main office already has parking
    const mainOfficeHasParking = locations.some(l => l.type === 'PARKING' && l.officeId === null);
    const defaultType: LocationType = mainOfficeHasParking ? 'ENTRANCE' : 'PARKING';

    setEditMode('add');
    setEditPinPosition(initialPosition);
    setFormData({ type: defaultType, name: '', note: '', officeId: null });
    setSelectedLocation(null);
  }, [locations]);

  const handleStartEdit = useCallback(() => {
    if (!selectedLocation) return;
    setEditMode('edit');
    setEditPinPosition({ lat: selectedLocation.latitude, lon: selectedLocation.longitude });
    setFormData({
      type: selectedLocation.type,
      name: selectedLocation.name || '',
      note: selectedLocation.note || '',
      officeId: selectedLocation.officeId,
    });
  }, [selectedLocation]);

  const handleCancelEdit = useCallback(() => {
    setEditMode(null);
    setEditPinPosition(null);
    setFormData({ type: 'PARKING', name: '', note: '', officeId: null });
  }, []);

  const handleMarkerClick = useCallback((location: ClientLocation) => {
    if (editMode) return;
    setSelectedLocation(prev => prev?.id === location.id ? null : location);
  }, [editMode]);

  const handleDeleteLocation = useCallback(() => {
    if (!selectedLocation) return;
    if (window.confirm('Удалить эту точку?')) {
      deleteMutation.mutate(selectedLocation.id);
    }
  }, [selectedLocation, deleteMutation]);

  const handleSubmit = useCallback(() => {
    if (!editPinPosition || !formData.name.trim()) return;

    if (editMode === 'add') {
      createMutation.mutate({
        type: formData.type,
        name: formData.name.trim(),
        latitude: editPinPosition.lat,
        longitude: editPinPosition.lon,
        note: formData.note.trim() || undefined,
        officeId: formData.officeId,
      });
    } else if (editMode === 'edit' && selectedLocation) {
      updateMutation.mutate({
        id: selectedLocation.id,
        data: {
          type: formData.type,
          name: formData.name.trim(),
          latitude: editPinPosition.lat,
          longitude: editPinPosition.lon,
          note: formData.note.trim() || undefined,
          officeId: formData.officeId,
        },
      });
    }
  }, [editMode, editPinPosition, formData, selectedLocation, createMutation, updateMutation]);

  // Search handlers
  const handleSearch = useCallback(async (query: string) => {
    setSearchQuery(query);
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const result = await searchAddress(query, {
        lat: 53.9,
        lon: 27.56,
        limit: 5,
      });
      setSearchResults(result.features);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSelectSearchResult = useCallback((result: GeocodingResult) => {
    const [lon, lat] = result.geometry.coordinates;
    setEditPinPosition({ lat, lon });
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  }, []);

  const formatSearchResult = useCallback((result: GeocodingResult): string => {
    const p = result.properties;
    if (p.display_name) {
      const parts = p.display_name.split(', ').slice(0, 3);
      return parts.join(', ');
    }
    return 'Неизвестный адрес';
  }, []);

  const getNavigationCoords = useCallback((): { lat: number; lon: number } | null => {
    if (selectedLocation) {
      return { lat: selectedLocation.latitude, lon: selectedLocation.longitude };
    }
    return null;
  }, [selectedLocation]);

  const handleNavigateGoogle = useCallback(() => {
    const coords = getNavigationCoords();
    if (!coords) return;
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${coords.lat},${coords.lon}`, '_blank');
  }, [getNavigationCoords]);

  const handleNavigateYandex = useCallback(() => {
    const coords = getNavigationCoords();
    if (!coords) return;
    window.open(`https://yandex.by/maps/?rtext=~${coords.lat},${coords.lon}&rtt=auto`, '_blank');
  }, [getNavigationCoords]);

  const handleNavigateInternal = useCallback(() => {
    const coords = getNavigationCoords();
    if (!coords) return;
    navigate(`/navigation?lat=${coords.lat}&lon=${coords.lon}&mode=${navMode}`);
  }, [getNavigationCoords, navigate, navMode]);

  // Get office name helper
  const getOfficeName = useCallback((officeId: string | null): string => {
    if (!officeId) return 'Основной офис';
    const office = offices.find(o => o.id === officeId);
    return office?.name || 'Неизвестный офис';
  }, [offices]);

  // Map center - use first location or Minsk
  const mapCenter = locations.length > 0
    ? { lat: locations[0].latitude, lon: locations[0].longitude }
    : { lat: 53.9, lon: 27.5667 };

  // Track which offices already have parking (for disabling PARKING option)
  // Key: officeId or '__main__' for main office
  const officesWithParking = useMemo(() => {
    const set = new Set<string>();
    for (const loc of locations) {
      if (loc.type === 'PARKING') {
        set.add(loc.officeId ?? '__main__');
      }
    }
    return set;
  }, [locations]);

  // Check if current office in form has parking (excluding selected location when editing)
  const currentOfficeHasParking = useMemo(() => {
    const officeKey = formData.officeId ?? '__main__';
    // When editing, don't count the selected location's parking
    if (editMode === 'edit' && selectedLocation?.type === 'PARKING' && (selectedLocation.officeId ?? '__main__') === officeKey) {
      return false;
    }
    return officesWithParking.has(officeKey);
  }, [formData.officeId, officesWithParking, editMode, selectedLocation]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="flex flex-col">
      {/* Map */}
      <div className="h-[45dvh] min-h-[300px] relative">
        <MapView
          center={mapCenter}
          locations={locations}
          clientLocation={null} // No more red marker
          selectedLocationId={selectedLocation?.id}
          selectedLocation={selectedLocation}
          onMarkerClick={handleMarkerClick}
          editMode={editMode}
          editPinPosition={editPinPosition}
          onEditPinChange={setEditPinPosition}
          editPinType={formData.type}
        />

        {/* Search UI (only in edit mode) */}
        {editMode && (
          <div className="absolute top-4 left-4 right-4 z-10">
            {isSearchOpen ? (
              <Card>
                <CardContent className="p-2">
                  <div className="flex gap-2">
                    <Input
                      autoFocus
                      className="h-9"
                      placeholder="Введите адрес..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setIsSearchOpen(false);
                        setSearchQuery('');
                        setSearchResults([]);
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {isSearching && (
                    <div className="flex items-center justify-center py-3">
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                  )}
                  {!isSearching && searchResults.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {searchResults.map((result, idx) => (
                        <button
                          key={idx}
                          className="w-full text-left px-3 py-2 text-sm rounded hover:bg-accent transition-colors"
                          onClick={() => handleSelectSearchResult(result)}
                        >
                          {formatSearchResult(result)}
                        </button>
                      ))}
                    </div>
                  )}
                  {!isSearching && searchQuery.length >= 3 && searchResults.length === 0 && (
                    <p className="text-sm text-muted-foreground py-2 text-center">
                      Ничего не найдено
                    </p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Button
                size="icon"
                variant="secondary"
                className="shadow-md"
                onClick={() => setIsSearchOpen(true)}
              >
                <Search className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Edit form (when in edit mode) */}
      {editMode && (
        <div className="p-4 space-y-3 border-t bg-background">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">
              {editMode === 'add' ? 'Новая точка' : 'Редактирование'}
            </h3>
            <Button size="icon" variant="ghost" onClick={handleCancelEdit}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Тип</Label>
              <Select
                value={formData.type}
                onValueChange={(v) => setFormData({ ...formData, type: v as LocationType })}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PARKING" disabled={currentOfficeHasParking}>
                    Парковка {currentOfficeHasParking && '(уже есть)'}
                  </SelectItem>
                  <SelectItem value="ENTRANCE">Вход</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Офис</Label>
              <Select
                value={formData.officeId ?? '__main__'}
                onValueChange={(v) => {
                  const newOfficeId = v === '__main__' ? null : v;
                  const officeKey = newOfficeId ?? '__main__';
                  // Auto-switch to ENTRANCE if new office already has parking
                  const newOfficeHasParking = officesWithParking.has(officeKey);
                  setFormData({
                    ...formData,
                    officeId: newOfficeId,
                    type: newOfficeHasParking && formData.type === 'PARKING' ? 'ENTRANCE' : formData.type,
                  });
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {offices.map((office) => (
                    <SelectItem key={office.id ?? '__main__'} value={office.id ?? '__main__'}>
                      {office.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Название *</Label>
            <Input
              className="h-9"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Например: У шлагбаума"
            />
          </div>

          <div>
            <Label className="text-xs">Примечание</Label>
            <Input
              className="h-9"
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              placeholder="Дополнительная информация"
            />
          </div>

          {editPinPosition && (
            <p className="text-xs text-muted-foreground">
              {editPinPosition.lat.toFixed(6)}, {editPinPosition.lon.toFixed(6)}
            </p>
          )}

          <div className="flex gap-2">
            <Button
              className="flex-1"
              onClick={handleSubmit}
              disabled={!editPinPosition || !formData.name.trim() || isSubmitting}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Check className="w-4 h-4 mr-2" />
              Сохранить
            </Button>
          </div>
        </div>
      )}

      {/* Location list (when NOT in edit mode) */}
      {!editMode && (
        <div className="p-4 space-y-3 border-t bg-background">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-sm">Точки ({locations.length})</h3>
            <Button size="sm" variant="outline" onClick={handleStartAdd}>
              <Plus className="w-4 h-4 mr-1" />
              Добавить
            </Button>
          </div>

          {locations.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Нет сохранённых точек
            </p>
          ) : (
            <div className="space-y-2">
              {locations.map((loc) => {
                const Icon = LOCATION_TYPE_ICONS[loc.type];
                const isSelected = selectedLocation?.id === loc.id;

                return (
                  <Card
                    key={loc.id}
                    className={`cursor-pointer transition-colors ${
                      isSelected ? 'ring-2 ring-primary' : ''
                    }`}
                    onClick={() => setSelectedLocation(isSelected ? null : loc)}
                  >
                    <CardContent className="p-3 flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: LOCATION_TYPE_COLORS[loc.type] + '20' }}
                      >
                        <Icon
                          className="w-4 h-4"
                          style={{ color: LOCATION_TYPE_COLORS[loc.type] }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {loc.name || LOCATION_TYPE_LABELS[loc.type]}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Building2 className="w-3 h-3" />
                          <span className="truncate">{getOfficeName(loc.officeId)}</span>
                        </div>
                        {isSelected && loc.note && (
                          <p className="text-xs text-muted-foreground mt-1 italic">
                            {loc.note}
                          </p>
                        )}
                      </div>
                      {isSelected && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEdit();
                            }}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteLocation();
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Navigation buttons */}
          {selectedLocation && (
            <div className="space-y-2 pt-2">
              {/* Mode selector and main navigation */}
              <div className="flex gap-2">
                <Select
                  value={navMode}
                  onValueChange={(v) => setNavMode(v as 'auto' | 'pedestrian')}
                >
                  <SelectTrigger className="w-24 h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">
                      <div className="flex items-center gap-1">
                        <Car className="w-4 h-4" />
                        <span>Авто</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="pedestrian">
                      <div className="flex items-center gap-1">
                        <Footprints className="w-4 h-4" />
                        <span>Пешком</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Button className="flex-1 h-10" onClick={handleNavigateInternal}>
                  <Navigation className="w-4 h-4 mr-2" />
                  Навигация
                </Button>
              </div>

              {/* External apps */}
              <div className="flex gap-2">
                <Button
                  className="flex-1 h-9"
                  variant="outline"
                  size="sm"
                  onClick={handleNavigateGoogle}
                >
                  Google Maps
                </Button>
                <Button
                  className="flex-1 h-9"
                  variant="outline"
                  size="sm"
                  onClick={handleNavigateYandex}
                >
                  Яндекс
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
